// @flow
// ----------------------------------------------------------------------
// <copyright file='Izenda.ReportDetail.js' company='Izenda'>
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import connect from 'share/enhancers/Izenda.Connect';
import classnames from 'classnames';
import Panel from 'share/components/controls/Izenda.Panel';
import ReportDetailFooter from './Izenda.ReportDetailFooter';
import ReportHistory from 'modules/Reports/ReportList/ReportHistory/Izenda.ReportHistory';
import convertToDatetimeFormat from 'share/utils/Izenda.ConvertToDateTimeFormat';
import CopyReport from './CopyReport/Izenda.CopyReport';
import TextHighLight from 'share/components/controls/Izenda.TextHighLight';
import ReportSubscribe from 'modules/Reports/ReportSubscribe/Izenda.ReportSubscribe';
import routeUtils from 'share/utils/Izenda.RouteUtils';
import AuthorizationValidator from 'share/components/hoc/Izenda.AuthorizationValidator';
import AuthorizationConstants from 'logic/data/Izenda.AuthorizationConstants';
import uiState from 'logic/Izenda.UiState';
import { isEmpty, isNumber, assign, get, toString, head } from 'lodash/fp';
import constants from 'logic/data/Izenda.ReportsConstants';
import BoardDetailHeader from 'share/components/controls/Izenda.BoardDetailHeader';
const SearchCriteria = constants.SEARCH_CRITERIA;
const ReportDetailHeader = BoardDetailHeader(constants);

const ViewHistoryIcon = AuthorizationValidator()((props: any) => (
  <a onClick={props.onClick} title='View History'>
    <span className='icon-history' />
  </a>
));

@connect()
export default class ReportDetail extends Component {
  static propTypes = {
    store: PropTypes.object
  };

  /*
   * Open report history popup
   */
  viewHistory = (success: boolean) => {
    const props = this.props;
    const {
      store,
      reportSubCategory,
      router,
      isGlobal,
      boardDetail: { id: reportId }
    } = props;

    if (success) {
      props
        .openPopup(ReportHistory, {
          showCopy: this.viewCopyPopup,
          showMove: this.viewMovePopup,
          reportId,
          masterStore: store,
          isGlobal
        })
        .then((reportId: string) => {
          if (reportId) {
            store.removeReport(reportId, reportSubCategory).then(function(result) {
              !isEmpty(result) && router.push(routeUtils.getReportUrl());
            });
          }
        });
    }
  };

  renderItemInfo = (text: string, value: any, field: any) => {
    const {
      store: { items: model },
      boardDetail
    } = this.props;
    return (
      <div className='form-group row'>
        <label className='col-xs-6 control-label u_textGrayColor'>{text}: </label>
        <div className='col-xs-6'>
          <TextHighLight
            highlight={model.searchText}
            text={toString(value)}
            notHighlight={boardDetail.isNotHighLight(model.searchType, field)}
          />
        </div>
      </div>
    );
  };

  viewCopyPopup = (version: Number) => {
    this.openCopyReportPopup(version, false);
  };

  viewMovePopup = (version: Number) => {
    this.openCopyReportPopup(version, true);
  };

  openCopyReportPopup = (version: Number, isMove: boolean) => {
    const props = this.props;
    const { store, boardDetail } = props;
    const latestVersion = 0;
    props
      .openPopup(CopyReport, {
        store,
        boardDetail,
        isMoveReport: isMove,
        version: isNumber(version) ? version : latestVersion
      })
      .then((message: string) => {
        this.onCloseCopyPopup(message, isMove);
      });
  };

  onCloseCopyPopup = (message: string, isMove: boolean) => {
    const props = this.props;
    const { store, loader, boardDetail, router } = props;

    if (message) {
      return props
        .confirmFn('Information', {
          description: message,
          type: 'info',
          showCloseIndicator: true
        })
        .then(() => {
          loader.startLoading();
          if (isMove) {
            router.push(routeUtils.getReportUrl('List', { reportType: boardDetail.type }));
          } else {
            router.push(
              routeUtils.getReportUrl(
                'List',
                assign(
                  { reportType: boardDetail.type },
                  store.selectedCategoryId && { catId: store.selectedCategoryId }
                )
              )
            );
          }
          return null;
        })
        .finally(loader.endLoading.bind(loader));
    }
  };

  closeSubcribe = () => {
    this.props.boardDetail.toggleSubscribeMode();
  };

  checkValidReport = () => {
    const props = this.props;
    const {
      store,
      boardDetail: { id: reportId }
    } = props;
    return store.checkValidReportId(reportId).then((result: any) => {
      if (!result.success && !isEmpty(result.messages)) {
        const message = head(result.messages) || {};
        const errorMessage = head(message.messages) || '';
        props.confirmMessage('error', errorMessage);
      }
      return result.success;
    });
  };

  viewReportHistory = () => {
    this.checkValidReport().then(this.viewHistory);
  };

  render() {
    const props = this.props;
    const {
      store,
      reportSubscriptionStore,
      boardDetail,
      boardDetail: { id: reportId, isShowDetail: expanedOption, accessPriority, isSubscribeMode },
      authorization,
      authorizationConstants: { PERMISSION_KEY: permissionKeys },
      reportSubCategory
    } = props;
    const renderItemInfo = this.renderItemInfo;
    const detailUri = {
      pathname: routeUtils.getReportUrl('View', { id: reportId }),
      state: {
        fromList: true
      }
    };

    const header = {
      store,
      boardDetail,
      expanedOption,
      component: ReportDetailHeader,
      detailUri,
      checkValidReport: this.checkValidReport,
      accessPriority,
      hasRenamePermission: authorization.checkPrivileges([permissionKeys.ReportsActionsOverwriteExistingReport])
    };

    const footer = {
      store,
      boardDetail,
      reportSubCategory,
      component: ReportDetailFooter,
      checkValidReport: this.checkValidReport,
      reportSubscriptionStore,
      viewCopyPopup: this.viewCopyPopup,
      viewMovePopup: this.viewMovePopup
    };

    const DetailBody = (props: any) => {
      const boardDetail = props.boardDetail || {};
      const localization = props.localization;
      return (
        <div className='row'>
          <div className='col-md-6 col-xs-12 report-description'>{boardDetail.description}</div>
          <div className='col-md-6 col-xs-12 report-detail-info'>
            <div className='row'>
              <div className='col-md-6'>
                {renderItemInfo(localization.getText('REPORT_LABEL_REPORT_OWNER'), boardDetail.owner, 'none')}
                {renderItemInfo(
                  localization.getText('COMMON_CRITERIA_CREATED_BY'),
                  boardDetail.createdBy,
                  SearchCriteria[3].value
                )}
                {renderItemInfo(
                  localization.getText('COMMON_CRITERIA_EDITED_BY'),
                  boardDetail.modifiedBy,
                  SearchCriteria[5].value
                )}
                {renderItemInfo(
                  localization.getText('COMMON_CRITERIA_NUMBER_OF_VIEWS'),
                  boardDetail.numberOfView,
                  'none'
                )}
                {renderItemInfo(
                  localization.getText('COMMON_CRITERIA_AVERAGE_RENDERING_TIME'),
                  boardDetail.renderingTimeInSeconds,
                  'none'
                )}
              </div>
              <div className='col-md-6'>
                <div className='form-group row'>
                  <label className='col-xs-6 control-label u_textGrayColor'>
                    {localization.getText('REPORT_LABEL_VERSION')}
                  </label>
                  <div className='col-xs-6'>
                    {' '}
                    {boardDetail.version}
                    {!boardDetail.type && (
                      <ViewHistoryIcon
                        onClick={this.viewReportHistory}
                        keys={[AuthorizationConstants.PERMISSION_KEY.ReportsActionsViewReportHistory]}
                      />
                    )}
                  </div>
                </div>
                {renderItemInfo(
                  localization.getText('COMMON_CRITERIA_CREATED_DATE'),
                  convertToDatetimeFormat(uiState.currentUser.dateFormat, boardDetail.created),
                  SearchCriteria[4].value
                )}
                {renderItemInfo(
                  localization.getText('COMMON_CRITERIA_EDITED_DATE'),
                  convertToDatetimeFormat(uiState.currentUser.dateFormat, boardDetail.modified),
                  SearchCriteria[6].value
                )}
                {renderItemInfo(
                  localization.getText('COMMON_CRITERIA_LAST_VIEWED'),
                  convertToDatetimeFormat(uiState.currentUser.dateFormat, boardDetail.lastViewed),
                  'none'
                )}
              </div>
            </div>
          </div>
        </div>
      );
    };

    return (
      <Panel
        renderCollapseContent
        header={header}
        initState={get('expanedOption', header)}
        footer={!isSubscribeMode && footer}
        collapsible
        className={classnames('report-detail', props.className)}
      >
        {isSubscribeMode ? (
          <ReportSubscribe
            reportId={boardDetail.id}
            reportType={boardDetail.type}
            subscribedInstancesStore={reportSubscriptionStore}
            onClose={this.closeSubcribe}
          />
        ) : (
          <DetailBody boardDetail={boardDetail} localization={props.localization} />
        )}
      </Panel>
    );
  }
}



// WEBPACK FOOTER //
// ./ui/modules/Reports/ReportList/Izenda.ReportDetail.jsx